/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  SidebarInset,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import React, { ReactNode, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useRouter } from 'next/router';
import AvailabilitySwitch from '@/components/AvailabilitySwitch/availability-switch.component';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/reducers';
import UserService from '@/services/user/user.service';
import { REALTIME_WIDGET_AVAILABILITY, SHOW_SNACKBAR } from '@/store/types';
import { styles } from '@material-ui/pickers/views/Calendar/Calendar';
import { InfoIcon, CrossIcon, CircleHelp, TriangleAlert } from 'lucide-react';
import { HELP_CENTER_LINK_URL } from '@/utils/constants';
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Tooltip as ShadcnTooltip,
} from '@/components/ui/tooltip';
import ChromePerfIcon from '../../../public/icons/chrome-perf-ss.svg';

type Crumb = { text: string; href?: string };

type Props = {
  children: ReactNode;
  setShowReportIssueBox: Function;
};

const MainArea = ({ children, setShowReportIssueBox }: Props) => {
  const { open, isMobile } = useSidebar();
  const { asPath } = useRouter();
  const dispatch = useDispatch();

  const { realtimeWidget, isTaggedToRealtimeEvent } = useSelector(
    (state: RootState) => state.common,
  );

  const { availability } = realtimeWidget;

  const [mounted, setMounted] = useState(false);
  const [isAvailable, setIsAvailable] = useState(Boolean(availability));
  const [showChromeExtensionModal, setShowChromeExtensionModal] =
    useState<boolean>(false);

  const chromeExtensionModalIgnoreClickHandler = () => {
    setShowChromeExtensionModal(false);
    localStorage.setItem('availability_toggle', JSON.stringify({ count: 2 }));
  };

  const toggleInstantMeetingAvailability = async () => {
    const audio = new Audio('/assets/sweet_text.ogg');
    audio.muted = true;
    audio.play();
    try {
      const realtime_meeting_availaibility = !isAvailable;
      const userData: any =
        localStorage && JSON.parse(localStorage.getItem('user_data'));
      let count =
        JSON.parse(localStorage.getItem('availability_toggle'))?.count || 0;
      if (realtime_meeting_availaibility) {
        localStorage.setItem(
          'availability_toggle',
          JSON.stringify({ count: ++count }),
        );
      }
      await UserService.update(userData?.id, {
        meta: { realtime_meeting_availaibility },
      });
      setIsAvailable(realtime_meeting_availaibility);
      dispatch({
        type: REALTIME_WIDGET_AVAILABILITY,
        payload: {
          availability: realtime_meeting_availaibility,
          userInteracted: true,
        },
      });
    } catch (err) {
      dispatch({
        type: SHOW_SNACKBAR,
        payload: {
          type: 'error',
          message: 'Failed to toggle your availability. Please try again.',
        },
      });
    }
  };

  const crumbs: Crumb[] = React.useMemo(() => {
    const base: Crumb[] = [];

    if (
      asPath?.includes('/insights/l2') ||
      asPath?.includes('/insights/details')
    ) {
      base.push({ text: 'Conversations' });
      base.push({ text: 'Lead Details' });
      return base;
    } else if (asPath?.includes('/workspace')) {
      base.push({ text: 'Setup' });
      return base;
    } else if (asPath?.includes('/reports')) {
      base.push({ text: 'Conversations Insights' });
      return base;
    } else if (asPath?.includes('/dashboard/admin')) {
      base.push({ text: 'All Conversations' });
      return base;
    } else if (asPath?.includes('/dashboard')) {
      base.push({ text: 'My Conversations' });
      return base;
    } else if (asPath?.includes('/shared')) {
      base.push({ text: 'My Conversations' });
      base.push({ text: 'Shared with Me' });
      return base;
    }

    return base;
  }, [asPath]);

  useEffect(() => {
    setIsAvailable(Boolean(availability));
  }, [availability]);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <SidebarInset
      className={cn('w-full h-screen', {
        'ml-64 max-w-[calc(100dvw-256px)]': open && !isMobile,
        'ml-10 max-w-[calc(100dvw-40px)]': !open && !isMobile,
      })}
    >
      <header
        className="
        w-full flex h-14 shrink-0 items-center gap-2 
        transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 
        border-b border-b-gray-200
        "
      >
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          {mounted && (
            <Breadcrumb>
              <BreadcrumbList>
                {crumbs.map((crumb, i) => {
                  const isLast = i === crumbs.length - 1;
                  return (
                    <BreadcrumbItem key={i}>
                      <BreadcrumbPage>{crumb.text}</BreadcrumbPage>
                      {!isLast && (
                        <BreadcrumbSeparator className="mx-2 hidden md:inline-block" />
                      )}
                    </BreadcrumbItem>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>
          )}
        </div>

        <div className="flex items-center ml-auto">
          <span>
            {realtimeWidget?.enabled && isTaggedToRealtimeEvent && (
              <TooltipProvider delayDuration={0}>
                <ShadcnTooltip>
                  <TooltipTrigger asChild>
                    <div className="flex">
                      <AvailabilitySwitch
                        className=""
                        screen="toggleAvailability"
                        handleOnChange={toggleInstantMeetingAvailability}
                        isAvailable={isAvailable}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    align="center"
                    side="left"
                    className="bg-black p-2.5 rounded-lg text-xs font-bold text-white whitespace-normal"
                  >
                    Toggle your availability for instant meeting requests
                  </TooltipContent>
                </ShadcnTooltip>
              </TooltipProvider>
            )}
          </span>
          <span className="flex items-center cursor-pointer">
            {realtimeWidget?.enabled && isTaggedToRealtimeEvent && (
              <span
                role="button"
                tabIndex={0}
                className="ml-2  mr-4"
                onClick={() => {
                  setShowChromeExtensionModal(prevState => !prevState);
                }}
                onKeyDown={() => {}}
              >
                <InfoIcon className="w-4 h-4" />
              </span>
            )}
          </span>

          <TooltipProvider delayDuration={0}>
            <ShadcnTooltip>
              <TooltipTrigger asChild>
                <a
                  href={HELP_CENTER_LINK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 border border-[#E4E4E7] rounded-md cursor-pointer"
                >
                  <CircleHelp size={16} />
                </a>
              </TooltipTrigger>
              <TooltipContent
                align="center"
                side="top"
                className="bg-black p-2.5 rounded-lg text-xs font-bold text-white whitespace-normal"
              >
                Help Center
              </TooltipContent>
            </ShadcnTooltip>
          </TooltipProvider>

          <TooltipProvider delayDuration={0}>
            <ShadcnTooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => {
                    setShowReportIssueBox(true);
                  }}
                  className="p-2 ml-2 mr-6 border border-[#E4E4E7] rounded-md cursor-pointer"
                >
                  <TriangleAlert size={16} />
                </button>
              </TooltipTrigger>
              <TooltipContent
                align="center"
                side="top"
                className="bg-black p-2.5 rounded-lg text-xs font-bold text-white whitespace-normal"
              >
                Report an issue
              </TooltipContent>
            </ShadcnTooltip>
          </TooltipProvider>
        </div>
      </header>
      <div className="h-full w-full overflow-auto max-h-screen-minus-16">
        {children}
      </div>

      {showChromeExtensionModal && (
        <div
          className={`${styles['tooltip']} fixed top-12 z-20 rounded mx-2 right-4 bg-primary text-white text-base leading-5`}
        >
          <div className={styles['tooltip-arrow']} />

          <div
            className="relative px-3 py-2 text-sm"
            style={{ width: '700px' }}
          >
            <div>
              <div className="font-bold">
                Please complete the below steps to ensure you get instant
                meeting alerts on your Chrome browser
              </div>
              <div className="mt-4 font-normal ">
                <div className="pl-1">
                  1. Install Zipteams Chrome Extension to get instant meeting
                  alerts across on any tab that you working on Chrome.{' '}
                  <a
                    href="https://chrome.google.com/webstore/search/zipteams"
                    target="_blank"
                    rel="noreferrer"
                    className="font-bold underline"
                  >
                    Install Now.
                  </a>
                </div>
                <div className="mt-3 pl-1 pb-1">
                  2. Add{' '}
                  <a
                    className="font-bold"
                    href="https://app.zipteams.com"
                    target="_blank"
                    rel="noreferrer"
                  >
                    app.zipteams.com
                  </a>{' '}
                  to always active in Chrome &gt; Settings &gt; Performance as
                  shown below.{' '}
                  <a
                    className="font-bold underline"
                    href="https://blog.google/products/chrome/new-chrome-features-to-save-battery-and-make-browsing-smoother/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Click here
                  </a>{' '}
                  to know more.
                </div>
                <ChromePerfIcon />
              </div>
            </div>
            <button
              type="button"
              className="bg-transparent font-bold text-xs leading-3.5 flex justify-center items-center p-1 cursor-pointer rounded-full absolute top-1 right-1"
              onClick={() => {
                chromeExtensionModalIgnoreClickHandler();
              }}
            >
              <CrossIcon className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </SidebarInset>
  );
};

export default MainArea;
